import { useState } from 'react';
import { STRINGS } from '@/strings/strings';

/**
 * FAQ accordion at the paywall. Bau-Spez §6 line 374 lists a FAQ
 * as part of Bildschirm 5.
 *
 * Eight entries per Sebastian's 2026-08-15 authorship (adapted from
 * DreamStories' order modal):
 *   wie-funktioniert-das · fuer-welches-alter · wie-gross-ist-das-buch
 *   wer-kann-vorkommen · haustier · welche-fotos · lieferzeit ·
 *   nicht-zufrieden
 *
 * Two filters guard the render (both drop the entry to null; the
 * accordion block itself renders null if every entry is dropped):
 *
 *   (a) Empty-entry suppression — either `q` or `a` is empty
 *       string. Reserved for future questions added before their
 *       answers exist. Round-1 all eight entries are populated.
 *
 *   (b) `[●]`-marker suppression — the `a` contains the literal
 *       marker `[●]` used to name a real gap in the text (e.g. the
 *       review-deadline number in `wie-funktioniert-das` is still
 *       undecided; the same number is open in the Nutzungsbedingungen).
 *       Rendering an entry with a bracket in the customer-facing copy
 *       would ship a placeholder at the paywall — the exact thing
 *       Sebastian ruled against 2026-08-15. When the number lands the
 *       marker is replaced with the digit + "Tage" and the entry
 *       appears. Tracker: [book-faq-review-deadline-marker].
 *
 * Order rendered follows the key order in STRINGS.book.bestellung.faq
 * (JavaScript's Object.entries preserves insertion order for
 * string keys — that's an ES2015 spec guarantee, not an
 * implementation detail).
 */
const INCOMPLETE_MARKER = '[●]';

/**
 * Format integer cents as a German-locale price string:
 * 750 → "7,50 €". Same shape as the helper in BuchartTiles;
 * kept local for the same self-contained rationale.
 */
function formatEuros(cents: number): string {
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
}

export interface FaqAccordionProps {
  /**
   * Sebastian ruling 2026-09-25 (Webflow shipping-price update).
   * Substituted into the `{shipping}` placeholder inside every
   * FAQ answer that mentions the delivery charge (today: the
   * "wie-gross-ist-das-buch" entry). Sourced from
   * `envelope.shippingCents` at the render site.
   */
  readonly shippingCents: number;
}

export function FaqAccordion({ shippingCents }: FaqAccordionProps) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const formattedShipping = formatEuros(shippingCents);
  const entries = Object.entries(STRINGS.book.bestellung.faq)
    .map(([key, entry]) => ({
      key,
      q: entry.q.replace('{shipping}', formattedShipping),
      a: entry.a.replace('{shipping}', formattedShipping),
    }))
    .filter(
      (entry) =>
        entry.q.length > 0 &&
        entry.a.length > 0 &&
        !entry.q.includes(INCOMPLETE_MARKER) &&
        !entry.a.includes(INCOMPLETE_MARKER),
    );

  if (entries.length === 0) {
    // Sebastian ruling: render NOTHING rather than a placeholder
    // accordion. If every entry is empty (pre-authorship state),
    // the FAQ block is absent entirely.
    return null;
  }

  return (
    <section className="space-y-2 pt-2">
      {entries.map((entry) => {
        const isOpen = openKey === entry.key;
        return (
          <div key={entry.key} className="rounded-lg border border-dream-navy/15">
            <button
              type="button"
              onClick={() => setOpenKey(isOpen ? null : entry.key)}
              aria-expanded={isOpen}
              className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-dream-navy hover:bg-dream-navy/5"
            >
              <span>{entry.q}</span>
              <span aria-hidden className="ml-2 text-dream-navy/50">
                {isOpen ? '−' : '+'}
              </span>
            </button>
            {isOpen && (
              <div className="border-t border-dream-navy/15 px-4 py-3 text-sm leading-relaxed text-dream-navy/80">
                {entry.a}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}
